<script setup lang="ts">
import type { NuxtError } from '#app'

/**
 * Custom error page
 *
 * Handles 404 and other error states with:
 * - Clear error message
 * - Helpful navigation to main service categories
 * - Links to home and help centre
 *
 * Uses default layout to maintain site header/footer.
 */

const props = defineProps<{
  error: NuxtError
}>()

const is404 = computed(() => props.error.statusCode === 404)

const headline = computed(() =>
  is404.value ? '404 — Page not found' : `${props.error.statusCode} — Something went wrong`
)

const message = computed(() =>
  is404.value
    ? "This page doesn't exist or may have moved. Browse our services below or contact us if you need assistance."
    : 'We encountered an unexpected error. Please try again or contact us if the problem persists.'
)

// Main service categories for helpful navigation
const serviceCategories = [
  {
    title: 'Security Services',
    description: 'CCTV, access control, and monitoring for Australian businesses.',
    href: '/security-services',
    icon: 'lucide:shield-check',
  },
  {
    title: 'Internet & Data',
    description: 'Business NBN, enterprise ethernet, and connectivity solutions.',
    href: '/internet-data',
    icon: 'lucide:wifi',
  },
  {
    title: 'Voice & Video',
    description: 'VoIP, video conferencing, and unified communications.',
    href: '/voice-video',
    icon: 'lucide:phone',
  },
  {
    title: 'Managed IT',
    description: 'Ongoing support, monitoring, and infrastructure management.',
    href: '/managed-it-services',
    icon: 'lucide:server',
  },
]

// Clear error and navigate home
function handleGoHome() {
  clearError({ redirect: '/' })
}

/**
 * SEO for error pages
 *
 * Error page policy:
 * - 4xx (404/410/etc): noindex. Genuine missing pages must not appear in search.
 * - 5xx: NO robots meta and NO noindex X-Robots-Tag. A transient server
 *   error must not cause Google to de-index a real page.
 * - Canonical is intentionally OMITTED to avoid misleading search engines.
 *
 * Note: Error pages in Nuxt may bypass app.vue, so we set robots explicitly here.
 */
const isClientError = computed(() => {
  const sc = props.error.statusCode
  return sc >= 400 && sc < 500
})

useHead({
  title: is404.value ? 'Page Not Found | Metwide' : 'Error | Metwide',
  meta: computed(() =>
    isClientError.value
      ? [{ name: 'robots', content: 'noindex, nofollow' }]
      : []
  ),
})
</script>

<template>
  <NuxtLayout>
    <!-- Error Hero Section -->
    <section class="bg-white py-16 sm:py-20">
      <div class="mx-auto max-w-7xl px-8">
        <div class="max-w-2xl">
          <h1 class="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            {{ headline }}
          </h1>
          <p class="mt-4 text-lg leading-7 text-neutral-600">
            {{ message }}
          </p>
          <div class="mt-8 flex flex-wrap items-center gap-4">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-full bg-primary-400 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              @click="handleGoHome"
            >
              <Icon name="lucide:home" size="20" />
              Back to Home
            </button>
            <NuxtLink
              to="/help-centre"
              class="inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-white px-6 py-3 text-base font-semibold text-slate-700 transition-colors hover:border-primary-500 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              <Icon name="lucide:life-buoy" size="20" />
              Help Centre
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Service Categories Section -->
    <section class="bg-neutral-50 py-16 sm:py-20">
      <div class="mx-auto max-w-7xl px-8">
        <h2 class="mb-10 text-center text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
          Browse Our Services
        </h2>
        <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <NuxtLink
            v-for="category in serviceCategories"
            :key="category.href"
            :to="category.href"
            class="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm ring-1 ring-neutral-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
          >
            <!-- Icon -->
            <div class="mb-4">
              <div
                class="flex size-12 items-center justify-center rounded-xl bg-mw-accent-50 transition-colors duration-300 group-hover:bg-mw-accent-100"
              >
                <Icon :name="category.icon" size="28" class="text-mw-accent-500" />
              </div>
            </div>

            <!-- Title -->
            <h3 class="mb-2 text-lg font-bold text-slate-900">
              {{ category.title }}
            </h3>

            <!-- Description -->
            <p class="text-sm leading-relaxed text-neutral-600">
              {{ category.description }}
            </p>

            <!-- Hover Accent Line -->
            <div
              class="absolute bottom-0 left-0 right-0 h-1 origin-left scale-x-0 bg-primary-400 transition-transform duration-300 group-hover:scale-x-100"
            ></div>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="bg-white py-12 sm:py-16">
      <div class="mx-auto max-w-7xl px-8 text-center">
        <p class="text-neutral-600">
          Need help finding something specific?
          <NuxtLink
            to="/contact"
            class="font-semibold text-primary-500 underline-offset-2 hover:text-primary-600 hover:underline"
          >
            Contact our team
          </NuxtLink>
          or call
          <LocalPhone
            class="font-semibold text-primary-500 underline-offset-2 hover:text-primary-600 hover:underline"
          />
        </p>
      </div>
    </section>
  </NuxtLayout>
</template>
